'use client';

import { useId, useState } from 'react';
import { useForm, useWatch, Controller } from 'react-hook-form';
import {
  Field,
  FieldContent,
  FieldError,
  Checkbox,
} from '@shared/shared-shadcn-ui';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  hcpFormValidationSchema,
  type HCPFormType,
} from '../../../../../packages/hcp/hcp';
import { SPECIALTY_OPTIONS } from './libs/constants';
import { cn } from '@shared/shared-utils';
import Link from 'next/link';
import {
  FormInput,
  FormSelect,
  FormSuccessDialog,
  FormErrorDialog,
} from './libs/components/components';
import { SelectOption } from '@shared/shared-select';
import { Button } from '../../../../../libs/components/components';
import ArrowRightIcon from '../../../../../../public/svg/arrow-right.svg';
import { HCP_FORM_DEFAULT_VALUES } from './libs/constants';
import { LazyRecaptcha } from '../../../../../libs/components/components';
import { useMutation } from 'react-query';
import { sendHcpFormData } from '../../../../../packages/hcp/hcp';

const HCPForm = () => {
  const hubspotCheckboxId = useId();
  const hubspotLabelId = `${hubspotCheckboxId}-label`;
  const shareInfoCheckboxId = useId();
  const shareInfoLabelId = `${shareInfoCheckboxId}-label`;

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false);
  const [recaptchaValue, setRecaptchaValue] = useState<string>('');
  const { register, handleSubmit, formState, control, setValue, reset } =
    useForm<HCPFormType>({
      resolver: zodResolver(hcpFormValidationSchema),
      mode: 'onChange',
      defaultValues: HCP_FORM_DEFAULT_VALUES,
    });

  const specialtyValue = useWatch({
    control: control,
    name: 'specialty',
  });

  const { mutateAsync, isLoading } = useMutation(sendHcpFormData, {
    onSuccess: () => {
      setIsDialogOpen(true);
      reset();
      setRecaptchaValue('');
    },
    onError: (error) => {
      console.error('Error submitting form:', error);
      setIsErrorDialogOpen(true);
    },
  });

  const onSubmit = async (data: HCPFormType) => {
    await mutateAsync(data);
  };

  const isDisabled = !formState.isValid || !recaptchaValue || isLoading;

  return (
    <>
      <FormSuccessDialog isOpen={isDialogOpen} onOpenChange={setIsDialogOpen} />
      <FormErrorDialog
        isOpen={isErrorDialogOpen}
        onOpenChange={setIsErrorDialogOpen}
      />
      <div
        id="physicianfinderform"
        className="w-full max-w-[1225px] py-12 pb-16 border-b-4 border-ogn-bright-yellow scroll-mt-[180px]"
      >
        <p className="text-[17px] font-normal text-ogn-blue lg:col-span-2 max-w-[1100px]">
          *Indicates a required field.
        </p>
        <form
          onSubmit={handleSubmit(onSubmit, (errors) => {
            console.log(errors, 'errors');
          })}
          className="w-full grid grid-cols-1 lg:grid-cols-2 gap-x-4 gap-y-6 items-start mt-4"
        >
          <Field data-invalid={!!formState.errors.firstName}>
            <FieldContent className="space-y-2">
              <FormInput
                {...register('firstName')}
                type="text"
                placeholder="First name*"
                hasError={!!formState.errors.firstName}
              />
              <FieldError className="min-h-[1.25rem]">
                {formState.errors.firstName && (
                  <div className="text-sm text-red-600">
                    {formState.errors.firstName.message}
                  </div>
                )}
              </FieldError>
            </FieldContent>
          </Field>

          <Field data-invalid={!!formState.errors.lastName}>
            <FieldContent className="space-y-2">
              <FormInput
                {...register('lastName')}
                type="text"
                placeholder="Last name*"
                hasError={!!formState.errors.lastName}
              />
              <FieldError className="min-h-[1.25rem]">
                {formState.errors.lastName && (
                  <div className="text-sm text-red-600">
                    {formState.errors.lastName.message}
                  </div>
                )}
              </FieldError>
            </FieldContent>
          </Field>

          <Field data-invalid={!!formState.errors.email}>
            <FieldContent className="space-y-2">
              <FormInput
                {...register('email')}
                type="email"
                placeholder="Email*"
                hasError={!!formState.errors.email}
              />
              <FieldError className="min-h-[1.25rem]">
                {formState.errors.email && (
                  <div className="text-sm text-red-600">
                    {formState.errors.email.message}
                  </div>
                )}
              </FieldError>
            </FieldContent>
          </Field>
          <Field data-invalid={!!formState.errors.specialty}>
            <FieldContent className="space-y-2">
              <FormSelect
                name="specialty"
                options={SPECIALTY_OPTIONS}
                placeholder="Provider Type/Specialty/Area of Interest* "
                onSelect={(option: SelectOption) =>
                  setValue('specialty', option.value, {
                    shouldValidate: true,
                  })
                }
                value={specialtyValue}
                className="h-[58px] "
              />
              <FieldError className="min-h-[1.25rem]">
                {formState.errors.specialty && (
                  <div className="text-sm text-red-600">
                    {formState.errors.specialty.message}
                  </div>
                )}
              </FieldError>
            </FieldContent>
          </Field>

          {/* NPI Number */}
          <Field data-invalid={!!formState.errors.npi}>
            <FieldContent className="space-y-2">
              <FormInput
                {...register('npi')}
                type="text"
                placeholder="NPI Number*"
                hasError={!!formState.errors.npi}
              />
              <FieldError className="min-h-[1.25rem]">
                {formState.errors.npi && (
                  <div className="text-sm text-red-600">
                    {formState.errors.npi.message}
                  </div>
                )}
              </FieldError>
            </FieldContent>
          </Field>

          <Field data-invalid={!!formState.errors.zip}>
            <FieldContent className="space-y-2">
              <FormInput
                {...register('zip')}
                type="text"
                placeholder="ZIP code"
                className="max-w-[50%] min-w-[150px] lg:max-w-[30%]"
                hasError={!!formState.errors.zip}
              />
              <FieldError className="min-h-[1.25rem]">
                {formState.errors.zip && (
                  <div className="text-sm text-red-600">
                    {formState.errors.zip.message}
                  </div>
                )}
              </FieldError>
            </FieldContent>
          </Field>

          <Field data-invalid={!!formState.errors.institution}>
            <FieldContent className="space-y-2">
              <FormInput
                {...register('institution')}
                type="text"
                placeholder="Institution or Affiliation"
                hasError={!!formState.errors.institution}
              />
              <FieldError className="min-h-[1.25rem]">
                {formState.errors.institution && (
                  <div className="text-sm text-red-600">
                    {formState.errors.institution.message}
                  </div>
                )}
              </FieldError>
            </FieldContent>
          </Field>

          {/* Institution Phone */}
          <Field data-invalid={!!formState.errors.phone}>
            <FieldContent className="space-y-2">
              <FormInput
                {...register('phone')}
                type="tel"
                placeholder="Institution or Affiliation Phone Number"
                hasError={!!formState.errors.phone}
              />
              <FieldError className="min-h-[1.25rem]">
                {formState.errors.phone && (
                  <div className="text-sm text-red-600">
                    {formState.errors.phone.message}
                  </div>
                )}
              </FieldError>
            </FieldContent>
          </Field>

          {/* Agreements */}
          <div className="space-y-3 pt-3 col-span-1 lg:col-span-2">
            <Field data-invalid={!!formState.errors.hubspotInfo}>
              <FieldContent>
                <div className="flex items-start space-x-6">
                  <Controller
                    control={control}
                    name="hubspotInfo"
                    render={({ field }) => (
                      <Checkbox
                        id={hubspotCheckboxId}
                        aria-labelledby={hubspotLabelId}
                        checked={field.value}
                        onCheckedChange={(checked) =>
                          field.onChange(checked === true)
                        }
                        className={cn(
                          'border-[3px] focus:outline-none w-[42px] h-[42px] rounded-[6px]',
                          formState.errors.hubspotInfo
                            ? '!border-red-500'
                            : 'border-[#0000001F]',
                        )}
                      />
                    )}
                  />
                  <label
                    id={hubspotLabelId}
                    htmlFor={hubspotCheckboxId}
                    className="text-ogn-blue cursor-pointer text-[18px] font-bold"
                  >
                    By checking below, you agree to allow Organogenesis and its
                    agents to collect the information provided and to be
                    contacted by Organogenesis and its agents about the
                    Organogenesis Physician Finder and in the future regarding
                    other products and services.
                  </label>
                </div>
                {formState.errors.hubspotInfo && (
                  <div className="text-sm text-red-600 mt-1">
                    {formState.errors.hubspotInfo.message}
                  </div>
                )}
              </FieldContent>
            </Field>

            <Field data-invalid={!!formState.errors.shareInfo}>
              <FieldContent>
                <div className="flex items-start space-x-6">
                  <div className="grid place-items-center mt-1">
                    <Controller
                      control={control}
                      name="shareInfo"
                      render={({ field }) => (
                        <Checkbox
                          id={shareInfoCheckboxId}
                          aria-labelledby={shareInfoLabelId}
                          checked={field.value}
                          onCheckedChange={(checked) =>
                            field.onChange(checked === true)
                          }
                          className={cn(
                            'border-[3px] focus:outline-none w-[42px] h-[42px] rounded-[6px]',
                            formState.errors.shareInfo
                              ? '!border-red-500'
                              : 'border-[#0000001F]',
                          )}
                        />
                      )}
                    />
                  </div>
                  <label
                    id={shareInfoLabelId}
                    htmlFor={shareInfoCheckboxId}
                    className="text-ogn-blue cursor-pointer text-[18px] font-bold"
                  >
                    By checking below, you acknowledge and agree that your
                    information may be collected and processed by HubSpot, not
                    only for the purpose of responding to your inquiry or
                    providing requested services but also for HubSpot’s own
                    marketing and sales initiatives.
                  </label>
                </div>
                {formState.errors.shareInfo && (
                  <div className="text-sm text-red-600 mt-1">
                    {formState.errors.shareInfo.message}
                  </div>
                )}
              </FieldContent>
            </Field>
          </div>

          <div className="text-ogn-blue pt-3 col-span-1 lg:col-span-2 space-y-4">
            <p className="text-[16px] lg:text-[18px] font-bold">
              Organogenesis will not sell, rent, or otherwise distribute your
              name or any personally identifiable information outside of
              Organogenesis and its agents. Organogenesis will only use your
              information in accordance with our{' '}
              <Link
                href="https://www.organogenesis.com/privacy.html"
                target="_blank"
                rel="noopener noreferrer"
                className="font-bold underline hover:no-underline text-wcw-cyan"
              >
                Privacy Policy
              </Link>
              .
            </p>
            <p className="text-[16px] lg:text-[18px] font-bold">
              Your Privacy Rights as a{' '}
              <Link
                href="https://www.organogenesis.com/privacy.html#ca-privacy-rights"
                target="_blank"
                rel="noopener noreferrer"
                className="font-bold underline hover:no-underline text-wcw-cyan"
              >
                California Resident
              </Link>
              .
            </p>
            <p className="text-[16px] lg:text-[18px] font-bold">
              By submitting this form, Organogenesis, Inc. has my permission to
              list my name, specialty, phone, and practice location information
              within the Physician Finder database on{' '}
              <Link
                href="https://www.woundscantwait.com"
                target="_blank"
                rel="noopener noreferrer"
                className="font-bold underline hover:no-underline text-wcw-cyan"
              >
                http://www.woundscantwait.com
              </Link>
              .
            </p>
          </div>

          <div className="w-full col-span-1 lg:col-span-2 flex flex-col items-center lg:flex-row lg:justify-between gap-y-6 lg:gap-x-4 flex-wrap">
            <LazyRecaptcha
              siteKey={String(process.env.NEXT_PUBLIC_CAPCHA_SITE_KEY)}
              onChange={(value) => {
                setRecaptchaValue(value || '');
              }}
            />
            <Button
              type="submit"
              variant="blue"
              disabled={isDisabled}
              className={cn(
                'flex items-center justify-center gap-x-2 h-[60px] py-3 !text-[22px] lg:!text-[24px] w-[260px] lg:w-[300px] rounded-full',
                {
                  'bg-gray-400 hover:bg-gray-400 cursor-not-allowed':
                    isDisabled,
                },
              )}
              fancy={!isDisabled}
            >
              Submit
              <ArrowRightIcon className="w-[20px] h-[20px]" />
            </Button>
          </div>
        </form>
      </div>
    </>
  );
};

export { HCPForm };
